import { useState, useRef } from "react";
import { Email, LTLShipment, FTLShipment } from "@src/types/api";
import { emailShipmentsFtl, emailShipmentsLtl } from "@taskpane/utils/emailShipments";
import { useTaskpaneServices } from "@src/load-agent/app/TaskpaneServicesContext";
import type {
  PricingFetchResult,
  SaveLTLPricingRequest,
  SavePricingRequest,
} from "@src/load-agent/types/contracts";
import { statusHistoryIdentityPayload } from "@src/load-agent/office/resolveActorEmailForThread";

type ShipmentType = "LTL" | "FTL";
interface UsePricingSubmissionConfig<
  T extends LTLShipment | FTLShipment,
  TFormData extends Record<string, unknown>,
> {
  mode: ShipmentType;
  formData: TFormData;
  emailData: Email | null;
  clickedShipment: T | null;
  selectedMode: string | null;
  accessorials?: Array<{
    key: string;
    apiName: string;
    checked: boolean;
  }>;
  requiredFields: (keyof TFormData)[];
  additionalValidation?: (formData: TFormData) => Record<string, string>;
  onSuccess: (shipment: T, offers: unknown) => void;
}

export function usePricingSubmission<
  T extends LTLShipment | FTLShipment,
  TFormData extends Record<string, unknown> = Record<string, unknown>,
>({
  mode,
  formData,
  emailData,
  clickedShipment,
  selectedMode,
  accessorials,
  requiredFields,
  additionalValidation,
  onSuccess,
}: UsePricingSubmissionConfig<T, TFormData>) {
  const { loadApp } = useTaskpaneServices();
  const [loading, setLoading] = useState(false);
  const [overallError, setOverallError] = useState<string | null>(null);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const validateForm = (): {
    [key: string]: string;
  } => {
    const newErrors: {
      [key: string]: string;
    } = {};

    requiredFields.forEach((field) => {
      const val = formData[field];

      if (val == null || (typeof val === "string" && !val.trim())) {
        newErrors[field as string] = "This field is required";
      }
    });
    const extra = additionalValidation?.(formData) ?? {};

    return { ...newErrors, ...extra };
  };
  const createShipmentIfNeeded = async (
    internetMessageId: string
  ): Promise<{
    shipmentID: string;
    shipment: T | null;
  }> => {
    const id = clickedShipment?.shipmentId;

    if (id) {
      return { shipmentID: id, shipment: null };
    }
    if (mode === "LTL") {
      const { shipmentId, shipment } = await loadApp.createLTLShipment({
        internetMessageId,
        selectedMode,
        formData,
      });

      return { shipmentID: shipmentId, shipment: shipment as T };
    }
    const { shipmentId, shipment } = await loadApp.createFTLShipment({
      internetMessageId,
      selectedMode,
      formData,
    });

    return { shipmentID: shipmentId, shipment: shipment as T };
  };
  const savePricingData = async (
    internetMessageId: string,
    shipmentID: string,
    existingShipment: T | null
  ): Promise<void> => {
    const existing = existingShipment ?? clickedShipment;

    if (mode === "LTL") {
      const req: SaveLTLPricingRequest<TFormData> = {
        shipmentId: shipmentID,
        selectedMode,
        internetMessageId,
        formData,
        existingShipment: existing,
        ...(accessorials ? { accessorials } : {}),
      };

      await loadApp.saveLTLPricing(req);
      return;
    }
    const ftlReq: SavePricingRequest<TFormData> = {
      shipmentId: shipmentID,
      selectedMode,
      internetMessageId,
      formData,
      existingShipment: existing,
    };

    await loadApp.saveFTLPricing(ftlReq);
  };
  const fetchOffers = async (
    internetMessageId: string,
    shipmentID: string
  ): Promise<PricingFetchResult | void> => {
    const identity = statusHistoryIdentityPayload(emailData);

    if (mode === "LTL") {
      return await loadApp.getLTLPricing(shipmentID, internetMessageId, identity);
    } else {
      return await loadApp.getFTLPricing(shipmentID, internetMessageId, identity);
    }
  };
  const refreshEmailData = async (): Promise<Email> => {
    if (!emailData?.internetMessageId) {
      throw new Error("Cannot refresh: internetMessageId not available");
    }
    const refreshed = await loadApp.getEmails(emailData.internetMessageId);

    if (!refreshed) throw new Error("Failed to fetch email data");
    return refreshed;
  };
  const scrollToLoader = () => {
    if (loaderRef.current) {
      const offset = 100;
      const top = loaderRef.current.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({ top: top + offset, behavior: "smooth" });
    }
  };
  const submitPricing = async (setSelectedOffer: (value: string | null) => void) => {
    setOverallError(null);
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      return { errors: newErrors, success: false };
    }
    try {
      setSelectedOffer(null);
      setLoading(true);
      const internetMessageId = emailData?.internetMessageId;

      if (!internetMessageId) {
        setOverallError("No email data available — cannot submit pricing.");
        return { errors: {}, success: false };
      }
      const { shipmentID, shipment: newShipment } = await createShipmentIfNeeded(internetMessageId);

      await savePricingData(internetMessageId, shipmentID, newShipment);
      scrollToLoader();
      const pricingResult = (await fetchOffers(internetMessageId, shipmentID)) ?? {
        hasOffers: true,
      };
      const emailRefreshResponse = await refreshEmailData();
      let offers: unknown = null;

      if (mode === "LTL") {
        const s = emailRefreshResponse
          ? emailShipmentsLtl(emailRefreshResponse).find((s) => s.shipmentId === shipmentID)
          : undefined;

        offers = Array.isArray(s?.quotedRates) ? s.quotedRates : [];
      } else {
        const ftl = emailRefreshResponse
          ? emailShipmentsFtl(emailRefreshResponse).find((s) => s.shipmentId === shipmentID)
          : undefined;

        offers = ftl?.quotedRates ?? null;
      }
      const updatedShipment: T | undefined =
        mode === "LTL"
          ? ((emailRefreshResponse
              ? emailShipmentsLtl(emailRefreshResponse).find((s) => s.shipmentId === shipmentID)
              : undefined) as T | undefined)
          : ((emailRefreshResponse
              ? emailShipmentsFtl(emailRefreshResponse).find((s) => s.shipmentId === shipmentID)
              : undefined) as T | undefined);

      if (updatedShipment) {
        onSuccess(updatedShipment, offers);
      }
      if (!pricingResult.hasOffers) {
        setOverallError(pricingResult.message || "No offers returned from the API.");
      }
      return { errors: {}, success: true };
    } catch (error) {
      if (error instanceof Error) {
        setOverallError(error.message);
      } else if (typeof error === "string") {
        setOverallError(error);
      } else {
        setOverallError("An unexpected error occurred");
      }
      return { errors: {}, success: false };
    } finally {
      setLoading(false);
    }
  };

  return {
    submitPricing,
    loading,
    overallError,
    setOverallError,
    loaderRef,
  };
}
